import React, { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Tabs } from "antd";
import { AgentDetailSection } from "../../../../agentDetail.model";
import { EDIT_AGENT_TAB_QUERY_PARAM } from "../../../../agents.constants";
import {
    AGENT_DETAIL_TABS,
    AGENT_DETAIL_TAB_SLUGS,
    getAgentDetailTabFromSlug,
} from "./constants/agentDetailTabs";
import { AgentDetailActionButtons } from "./components/actionButtons/actionButtons.index";
import { LeftSection } from "./components/leftSection/leftSection.index";
import { RightSection } from "./components/rightSection/rightSection.index";
import styles from "./mainDetails.module.css";

interface MainDetailsProps {
    guardNavigation: (action: () => void) => void;
    isSaving: boolean;
}

export const MainDetails = ({ guardNavigation, isSaving }: MainDetailsProps) => {
    const [searchParams, setSearchParams] = useSearchParams();

    const activeTab = useMemo(
        () => getAgentDetailTabFromSlug(searchParams.get(EDIT_AGENT_TAB_QUERY_PARAM)),
        [searchParams]
    );

    const tabItems = useMemo(
        () => AGENT_DETAIL_TABS.map(({ key, label }) => ({ key, label, disabled: isSaving })),
        [isSaving]
    );

    const navigateToTab = useCallback(
        (key: AgentDetailSection) => {
            setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set(EDIT_AGENT_TAB_QUERY_PARAM, AGENT_DETAIL_TAB_SLUGS[key]);
                return next;
            });
        },
        [setSearchParams]
    );

    const handleTabChange = useCallback(
        (key: string) => {
            const nextTab = key as AgentDetailSection;
            if (nextTab === activeTab) return;

            guardNavigation(() => navigateToTab(nextTab));
        },
        [activeTab, guardNavigation, navigateToTab]
    );

    return (
        <div className={styles.outerCtn}>
            <div className={styles.headerCtn}>
                <Tabs
                    size="small"
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    items={tabItems}
                    className={styles.tabsRoot}
                    aria-busy={isSaving}
                    classNames={{
                        header: styles.tabsHeader,
                    }}
                />
                <AgentDetailActionButtons isSaving={isSaving} />
            </div>
            <div className={styles.splitLayout}>
                <div className={styles.leftSectionContainer}>
                    <LeftSection />
                </div>
                <div className={styles.rightSectionContainer}>
                    <RightSection activeTab={activeTab} />
                </div>
            </div>
        </div>
    );
};
